import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Divider, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { useProfile } from '@/entities/profile';
import {
  CompanyCrmIntegrationSettings,
  CompanyModulesSettings,
  useCompanyById,
} from '@/features/company';
import { usePageTitle } from '@/features/pageTitle';
import { NotFoundData } from '@/shared/ui/NotFoundData';
import { PageContainer } from '@/shared/ui';
import type { TTab } from '@/shared/ui/Tabs';
import { Tabs } from '@/shared/ui/Tabs';
import { getRoutePath } from '@/shared/utils/routes';

import { ChannelsOfCompanyTab } from './ChannelsOfCompanyTab';
import { KnowledgeBaseTab } from './KnowledgeBaseTab';
import { UsersOfCompanyListTab } from './UsersOfCompanyListTab';

type CompanySettingsTab = 'settings' | 'modules' | 'knowledge-base';

function getCompanySettingsTab(tab: string | undefined): CompanySettingsTab {
  if (tab === 'modules' || tab === 'knowledge-base') return tab;
  return 'settings';
}

export const CompanySettingsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { id: companyId, tab } = useParams<{ id: string; tab?: string }>();
  const { company } = useCompanyById(companyId);
  const { profile } = useProfile();
  const canManageModules = Boolean(
    company?.owner_id && profile?.account_id && company.owner_id === profile.account_id
  );
  const activeTab = getCompanySettingsTab(tab);

  usePageTitle(
    `${t('company:settingsPage.title')} ${company?.name ?? ''}`,
    true,
    getRoutePath('MAIN', 'COMPANIES')
  );

  if (!companyId) {
    return (
      <PageContainer>
        <NotFoundData title={t('company:companyNotFound')} />
      </PageContainer>
    );
  }

  const tabs: TTab[] = [
    {
      value: 'settings',
      label: t('company:settingsPage.tabs.settings'),
      content: (
        <Box
          sx={{
            mt: 2,
            border: '1px solid',
            borderColor: (theme) => alpha(theme.palette.primary.main, 0.18),
            borderTop: '3px solid',
            borderTopColor: 'primary.main',
            borderRadius: 2,
            bgcolor: 'common.white',
            boxShadow: '0 18px 44px rgba(28, 29, 34, 0.05)',
            overflow: 'hidden',
          }}
        >
          <Stack divider={<Divider />}>
            <Box sx={{ p: { xs: 2, md: 2.5 } }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr',
                  gap: { xs: 2.5, lg: 4 },
                  alignItems: 'flex-start',
                }}
              >
                <Stack spacing={1.25} sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="overline"
                    color="primary"
                    fontWeight={800}
                  >
                    {t('company:settingsPage.sections.integration')}
                  </Typography>
                  <CompanyCrmIntegrationSettings companyId={companyId} />
                </Stack>

                <Stack spacing={1.25} sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="overline"
                    color="primary"
                    fontWeight={800}
                  >
                    {t('company:settingsPage.sections.profile')}
                  </Typography>
                  <UsersOfCompanyListTab companyId={companyId} />
                </Stack>
              </Box>
            </Box>

            <Box sx={{ p: { xs: 2, md: 2.5 } }}>
              <Stack spacing={1.5}>
                <Typography
                  variant="overline"
                  color="primary"
                  fontWeight={800}
                >
                  {t('company:settingsPage.sections.channels')}
                </Typography>
                <ChannelsOfCompanyTab />
              </Stack>
            </Box>
          </Stack>
        </Box>
      ),
    },
    {
      value: 'modules',
      label: t('company:settingsPage.tabs.modules'),
      content: (
        <Box
          sx={{
            mt: 2,
            p: { xs: 2, md: 2.5 },
            border: '1px solid',
            borderColor: (theme) => alpha(theme.palette.primary.main, 0.18),
            borderTop: '3px solid',
            borderTopColor: 'primary.main',
            borderRadius: 2,
            bgcolor: 'common.white',
            boxShadow: '0 18px 44px rgba(28, 29, 34, 0.05)',
          }}
        >
          <CompanyModulesSettings
            companyId={companyId}
            canManageModules={canManageModules}
          />
        </Box>
      ),
    },
    {
      value: 'knowledge-base',
      label: t('company:settingsPage.tabs.knowledgeBase'),
      content: (
        <Box sx={{ mt: 2 }}>
          <KnowledgeBaseTab
            companyId={companyId}
            disabled={!canManageModules}
          />
        </Box>
      ),
    },
  ];

  return (
    <PageContainer>
      <Box sx={{ mt: 2 }}>
        <Tabs
          tabs={tabs}
          value={activeTab}
          onChange={(_, newValue) => {
            const nextTab = newValue as CompanySettingsTab;
            navigate(
              getRoutePath('MAIN', 'COMPANY_SETTINGS')
                .replace(':id', companyId)
                .replace(':tab', nextTab)
            );
          }}
        />
      </Box>
    </PageContainer>
  );
};
